/* HPN Listicle - Offer Card */
(() => {
  if (window.__hpnListicleOfferCardScriptLoaded) return;
  window.__hpnListicleOfferCardScriptLoaded = true;

  const SECTION_SELECTOR = '.hpn-listicle-offer-card';
  const CTA_SELECTOR = '[data-hpn-listicle-offer-cta]';
  const TIMER_SELECTOR = '[data-hpn-listicle-offer-timer]';

  function getShopifyRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path}`.replace(/\/{2,}/g, '/');
  }

  function normalizeDiscountCodes(rawCodes) {
    return String(rawCodes || '')
      .split(/[\n,]+/)
      .map((code) => code.trim())
      .filter(Boolean)
      .join(',');
  }

  async function fetchJson(url, options) {
    const response = await fetch(url, options);

    if (!response.ok) {
      let message = 'Something went wrong. Please try again.';

      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch (error) {
        // Keep the fallback message when the response is not JSON.
      }

      throw new Error(message);
    }

    return response.json();
  }

  async function clearCart() {
    await fetchJson(getShopifyRoute('cart/clear.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });
  }

  async function addMainItem(button) {
    const variantId = Number(button.getAttribute('data-variant-id'));
    const sellingPlanId = Number(button.getAttribute('data-selling-plan-id'));
    const quantity = Number.parseInt(button.getAttribute('data-quantity') || '1', 10);

    if (!Number.isInteger(variantId) || variantId <= 0) {
      throw new Error('Missing or invalid variant ID.');
    }

    const item = {
      id: variantId,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    };

    if (Number.isInteger(sellingPlanId) && sellingPlanId > 0) {
      item.selling_plan = sellingPlanId;
    }

    await fetchJson(getShopifyRoute('cart/add.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [item],
      }),
    });
  }

  const BOGO_RELOAD_MARKER = '_hpn_listicle_bogo_reload';

  async function fetchCart() {
    return fetchJson(getShopifyRoute('cart.js'), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cache: 'no-store',
    });
  }

  function normalizeBogoParamString(rawValue) {
    if (!rawValue) return '';

    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.includes('://')) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (error) {
        // Continue sanitizing the original value.
      }
    }

    const queryStartIndex = normalized.indexOf('?');

    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');

    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function isInternalBogoParam(key) {
    return key === BOGO_RELOAD_MARKER;
  }

  function getConfiguredBogoParams(button) {
    if (!button || button.getAttribute('data-bogo-url-params-enabled') !== '1') {
      return '';
    }

    return normalizeBogoParamString(
      button.getAttribute('data-bogo-url-params') || ''
    );
  }

  function isSubscriptionCheckout(button) {
    return button?.getAttribute('data-purchase-type') === 'subscription';
  }

  function configuredBogoParamsMatchUrl(button, sanitizedParams) {
    const incoming = new URLSearchParams(
      sanitizedParams || getConfiguredBogoParams(button)
    );
    const current = new URLSearchParams(window.location.search);
    let matches = true;

    incoming.forEach((value, key) => {
      if (!key) return;

      if (current.get(key) !== value) {
        matches = false;
      }
    });

    return matches;
  }

  function mergeBogoParamsIntoUrl(baseUrl, sanitizedParams) {
    const mergedUrl = new URL(baseUrl, window.location.origin);
    const incomingParams = new URLSearchParams(sanitizedParams);
    let hasValidParam = false;

    incomingParams.forEach((value, key) => {
      if (!key) return;

      mergedUrl.searchParams.set(key, value);
      hasValidParam = true;
    });

    return hasValidParam ? mergedUrl : null;
  }

  function replaceCurrentUrl(url) {
    const nextRelativeUrl =
      url.pathname +
      (url.search ? url.search : '') +
      url.hash;

    window.history.replaceState({}, '', nextRelativeUrl);
  }

  function stripInternalBogoParamsFromCurrentUrl() {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(BOGO_RELOAD_MARKER)) return;

    url.searchParams.delete(BOGO_RELOAD_MARKER);
    replaceCurrentUrl(url);
  }

  function applyBogoUrlParams(button, options = {}) {
    const sanitizedParams = getConfiguredBogoParams(button);
    const allowReload = Boolean(options.allowReload);

    if (!sanitizedParams || !isSubscriptionCheckout(button)) {
      return false;
    }

    if (configuredBogoParamsMatchUrl(button, sanitizedParams)) {
      stripInternalBogoParamsFromCurrentUrl();
      return false;
    }

    const mergedUrl = mergeBogoParamsIntoUrl(
      window.location.href,
      sanitizedParams
    );

    if (!mergedUrl) return false;

    if (allowReload) {
      const currentUrl = new URL(window.location.href);

      if (!currentUrl.searchParams.has(BOGO_RELOAD_MARKER)) {
        mergedUrl.searchParams.set(BOGO_RELOAD_MARKER, '1');
        window.location.replace(mergedUrl.toString());
        return true;
      }
    }

    mergedUrl.searchParams.delete(BOGO_RELOAD_MARKER);
    replaceCurrentUrl(mergedUrl);

    return false;
  }

  function removeConfiguredBogoParamsFromCurrentUrl(button) {
    const sanitizedParams = getConfiguredBogoParams(button);
    const url = new URL(window.location.href);
    let changed = false;

    if (sanitizedParams) {
      new URLSearchParams(sanitizedParams).forEach((value, key) => {
        if (!key || isInternalBogoParam(key)) return;

        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
    }

    if (url.searchParams.has(BOGO_RELOAD_MARKER)) {
      url.searchParams.delete(BOGO_RELOAD_MARKER);
      changed = true;
    }

    if (changed) {
      replaceCurrentUrl(url);
    }
  }

  function getBogoCartAttributes(button, clearValues = false) {
    const sanitizedParams = getConfiguredBogoParams(button);

    if (!sanitizedParams) return null;

    const attributes = {};
    let hasAny = false;

    new URLSearchParams(sanitizedParams).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      attributes[key] = clearValues ? '' : value;
      hasAny = true;
    });

    return hasAny ? attributes : null;
  }

  async function updateBogoCartAttributes(button, clearValues = false) {
    const attributes = getBogoCartAttributes(button, clearValues);

    if (!attributes) return null;

    return fetchJson(getShopifyRoute('cart/update.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes,
      }),
    });
  }

  function copyPublicSearchParams(sourceParams, targetUrl) {
    sourceParams.forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      targetUrl.searchParams.set(key, value);
    });
  }

  function mergeConfiguredBogoParams(button, targetUrl) {
    const sanitizedParams = getConfiguredBogoParams(button);

    if (!sanitizedParams) return;

    new URLSearchParams(sanitizedParams).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      targetUrl.searchParams.set(key, value);
    });
  }

  function removeConfiguredBogoParams(button, targetUrl) {
    const sanitizedParams = getConfiguredBogoParams(button);

    if (!sanitizedParams) return;

    new URLSearchParams(sanitizedParams).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      targetUrl.searchParams.delete(key);
    });
  }

  function getCheckoutUrl(button) {
    const discountCodes = normalizeDiscountCodes(
      button.getAttribute('data-discount-codes')
    );
    const checkoutUrl = new URL(
      getShopifyRoute('checkout'),
      window.location.origin
    );

    copyPublicSearchParams(
      new URLSearchParams(window.location.search),
      checkoutUrl
    );

    if (isSubscriptionCheckout(button)) {
      mergeConfiguredBogoParams(button, checkoutUrl);
    } else {
      removeConfiguredBogoParams(button, checkoutUrl);
    }

    checkoutUrl.searchParams.delete(BOGO_RELOAD_MARKER);

    if (discountCodes) {
      checkoutUrl.searchParams.set('discount', discountCodes);
    } else {
      checkoutUrl.searchParams.delete('discount');
    }

    if (checkoutUrl.origin === window.location.origin) {
      return (
        checkoutUrl.pathname +
        checkoutUrl.search +
        checkoutUrl.hash
      );
    }

    return checkoutUrl.toString();
  }

  function redirectToCheckout(button) {
    window.location.assign(getCheckoutUrl(button));
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      let elapsed = 0;
      const interval = 200;
      const maxWait = 3000;

      function checkCart() {
        fetchCart()
          .then((cart) => {
            if (
              cart &&
              typeof cart.item_count === 'number' &&
              cart.item_count > knownItemCount
            ) {
              resolve(true);
              return;
            }

            elapsed += interval;

            if (elapsed >= maxWait) {
              resolve(false);
              return;
            }

            window.setTimeout(checkCart, interval);
          })
          .catch(() => {
            elapsed += interval;

            if (elapsed >= maxWait) {
              resolve(false);
              return;
            }

            window.setTimeout(checkCart, interval);
          });
      }

      window.setTimeout(checkCart, interval);
    });
  }

  function showError(button, message) {
    const block = button.closest('.hpn-listicle-offer-card__block--cta');
    const errorEl = block ? block.querySelector('[data-hpn-listicle-offer-error]') : null;

    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function hideError(button) {
    const block = button.closest('.hpn-listicle-offer-card__block--cta');
    const errorEl = block ? block.querySelector('[data-hpn-listicle-offer-error]') : null;

    if (!errorEl) return;

    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  function redirectToAssignedLink(button) {
    const linkUrl = button.getAttribute('data-link-url');
    const openNewTab = button.getAttribute('data-link-new-tab') === '1';

    if (!linkUrl) {
      showError(button, 'Missing assigned link URL.');
      return;
    }

    if (openNewTab) {
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    window.location.assign(linkUrl);
  }

  async function handleCtaClick(button) {
    if (!button || button.classList.contains('is-loading')) return;

    const ctaBehavior = button.getAttribute('data-cta-behavior') || 'checkout';
    const loadingText = button.getAttribute('data-loading-text') || 'ADDING...';
    const contentEl = button.querySelector('[data-hpn-listicle-offer-cta-content]');
    const loadingEl = button.querySelector('[data-hpn-listicle-offer-cta-loading]');
    const shouldClearCart = button.getAttribute('data-clear-cart') === '1';
    const configError = button.getAttribute('data-config-error') || '';
    const isSubscription = isSubscriptionCheckout(button);
    const hasBogoParams = Boolean(getConfiguredBogoParams(button));
    const quantity = Number.parseInt(
      button.getAttribute('data-quantity') || '1',
      10
    );
    const expectedAddedQuantity =
      Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

    hideError(button);

    if (ctaBehavior === 'link') {
      redirectToAssignedLink(button);
      return;
    }

    if (configError) {
      showError(button, configError);
      return;
    }

    button.classList.add('is-loading');
    button.disabled = true;

    if (contentEl) {
      contentEl.style.display = 'none';
    }

    if (loadingEl) {
      loadingEl.textContent = loadingText;
      loadingEl.hidden = false;
    }

    try {
      if (isSubscription) {
        applyBogoUrlParams(button);
      } else {
        removeConfiguredBogoParamsFromCurrentUrl(button);
      }

      if (shouldClearCart) {
        await clearCart();
      }

      if (!isSubscription && hasBogoParams) {
        await updateBogoCartAttributes(button, true).catch(() => null);
      }

      let cartBeforeAdd = null;

      if (isSubscription && hasBogoParams) {
        cartBeforeAdd = await fetchCart().catch(() => null);
      }

      await addMainItem(button);

      if (isSubscription && hasBogoParams) {
        const cartAfterAdd = await fetchCart().catch(() => null);
        const cartAfterAttributeUpdate = await updateBogoCartAttributes(
          button,
          false
        ).catch(() => null);

        const latestCart = cartAfterAttributeUpdate || cartAfterAdd;
        const knownItemCount =
          latestCart && typeof latestCart.item_count === 'number'
            ? latestCart.item_count
            : null;
        const baselineItemCount =
          cartBeforeAdd && typeof cartBeforeAdd.item_count === 'number'
            ? cartBeforeAdd.item_count
            : null;
        const giftAlreadyPresent =
          knownItemCount !== null &&
          baselineItemCount !== null &&
          knownItemCount > baselineItemCount + expectedAddedQuantity;

        if (!giftAlreadyPresent && knownItemCount !== null) {
          await waitForBogoGift(knownItemCount);
        }
      }

      redirectToCheckout(button);
    } catch (error) {
      showError(button, error.message || 'Something went wrong. Please try again.');
      button.classList.remove('is-loading');
      button.disabled = false;

      if (loadingEl) {
        loadingEl.hidden = true;
      }

      if (contentEl) {
        contentEl.style.display = 'inline-flex';
      }
    }
  }

  function initBogoUrlParamsForRoot(root = document) {
    const buttons = [];

    if (root.matches?.(CTA_SELECTOR)) {
      buttons.push(root);
    }

    root.querySelectorAll?.(CTA_SELECTOR).forEach((button) => {
      buttons.push(button);
    });

    for (const button of buttons) {
      if (button.getAttribute('data-cta-behavior') !== 'checkout') continue;
      if (!getConfiguredBogoParams(button)) continue;

      if (isSubscriptionCheckout(button)) {
        if (applyBogoUrlParams(button, { allowReload: true })) {
          return;
        }
      } else {
        removeConfiguredBogoParamsFromCurrentUrl(button);
      }
    }
  }

  function bindBogoUrlParamLifecycle() {
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => initBogoUrlParamsForRoot(document),
        { once: true }
      );
    } else {
      initBogoUrlParamsForRoot(document);
    }

    document.addEventListener('shopify:section:load', (event) => {
      initBogoUrlParamsForRoot(event.target);
    });
  }

  bindBogoUrlParamLifecycle();

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function initTimer(timer) {
    if (!timer || timer.__hpnListicleOfferTimerBound) return;
    timer.__hpnListicleOfferTimerBound = true;

    let durationMinutes = Number.parseInt(timer.getAttribute('data-duration-minutes'), 10);
    const storageKey = timer.getAttribute('data-storage-key');

    const hoursEl = timer.querySelector('[data-hpn-listicle-offer-hours]');
    const minsEl = timer.querySelector('[data-hpn-listicle-offer-mins]');
    const secsEl = timer.querySelector('[data-hpn-listicle-offer-secs]');

    if (!Number.isFinite(durationMinutes) || durationMinutes < 1) {
      durationMinutes = 1;
    }

    let endTime = null;
    const now = Date.now();

    try {
      endTime = Number.parseInt(window.localStorage.getItem(storageKey), 10);
    } catch (error) {
      endTime = null;
    }

    if (!Number.isFinite(endTime) || endTime <= now) {
      endTime = now + durationMinutes * 60 * 1000;

      try {
        window.localStorage.setItem(storageKey, String(endTime));
      } catch (error) {
        // Continue with an in-memory timer when localStorage is unavailable.
      }
    }

    function renderTimer() {
      const remaining = Math.max(0, endTime - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minsEl) minsEl.textContent = pad(minutes);
      if (secsEl) secsEl.textContent = pad(seconds);

      if (remaining > 0) {
        timer.__hpnListicleOfferTimerId = window.setTimeout(renderTimer, 1000);
      }
    }

    renderTimer();
  }

  const GALLERY_SELECTOR = '[data-hpn-listicle-offer-gallery]';
  const GALLERY_SLIDE_SELECTOR = '[data-hpn-listicle-offer-gallery-slide]';
  const GALLERY_THUMB_SELECTOR = '[data-hpn-listicle-offer-gallery-thumb]';
  const GALLERY_THUMB_VIEWPORT_SELECTOR = '[data-hpn-listicle-offer-gallery-thumb-viewport]';
  const GALLERY_NAV_SELECTOR = '[data-hpn-listicle-offer-gallery-nav]';
  const GALLERY_VIDEO_SELECTOR = '[data-hpn-listicle-offer-gallery-video]';

  function preloadGalleryImage(img) {
    if (!img) return Promise.resolve();

    try {
      img.loading = 'eager';
      img.decoding = 'async';
      img.setAttribute('fetchpriority', 'high');
    } catch (error) {
      // Continue with the image's existing browser defaults.
    }

    const waitForImage = new Promise((resolve) => {
      if (img.complete) {
        resolve();
        return;
      }

      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });

    return waitForImage.then(() => {
      if (typeof img.decode !== 'function') return undefined;
      return img.decode().catch(() => undefined);
    });
  }

  function syncGalleryVideoFrameFit(video) {
    if (!video) return;

    const applyFit = () => {
      const videoWidth = Number(video.videoWidth || 0);
      const videoHeight = Number(video.videoHeight || 0);

      video.classList.remove(
        'is-portrait',
        'is-landscape',
        'is-square',
        'is-video-size-pending'
      );

      if (!videoWidth || !videoHeight) {
        video.classList.add('is-video-size-pending');
        return;
      }

      if (videoHeight > videoWidth) {
        video.classList.add('is-portrait');
      } else if (videoWidth > videoHeight) {
        video.classList.add('is-landscape');
      } else {
        video.classList.add('is-square');
      }
    };

    applyFit();

    if (video.__hpnListicleOfferGalleryFrameFitBound) return;
    video.__hpnListicleOfferGalleryFrameFitBound = true;

    video.addEventListener('loadedmetadata', applyFit);
    video.addEventListener('loadeddata', applyFit);
    video.addEventListener('resize', applyFit);
  }

  function preloadGalleryVideo(video) {
    if (!video) return Promise.resolve();

    try {
      video.autoplay = true;
      video.controls = true;
      video.preload = 'auto';
      video.setAttribute('playsinline', '');
      syncGalleryVideoFrameFit(video);
      video.load();
    } catch (error) {
      return Promise.resolve();
    }

    if (video.readyState >= 2) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      video.addEventListener('loadeddata', resolve, { once: true });
      video.addEventListener('canplay', resolve, { once: true });
      video.addEventListener('error', resolve, { once: true });
    });
  }

  function preloadGalleryMedia(slides) {
    const preloadTasks = [];

    slides.forEach((slide) => {
      slide.querySelectorAll('img').forEach((img) => {
        preloadTasks.push(preloadGalleryImage(img));
      });

      slide.querySelectorAll(GALLERY_VIDEO_SELECTOR).forEach((video) => {
        preloadTasks.push(preloadGalleryVideo(video));
      });
    });

    return Promise.allSettled(preloadTasks);
  }

  function pauseSlideVideos(slide) {
    if (!slide) return;

    slide.querySelectorAll(GALLERY_VIDEO_SELECTOR).forEach((video) => {
      try {
        video.pause();
      } catch (error) {
        // Ignore media pause errors.
      }
    });
  }

  function playSlideVideos(slide) {
    if (!slide) return;

    slide.querySelectorAll(GALLERY_VIDEO_SELECTOR).forEach((video) => {
      try {
        syncGalleryVideoFrameFit(video);
        video.removeAttribute('muted');
        video.muted = false;
        video.defaultMuted = false;
        video.setAttribute('playsinline', '');

        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            // Browsers can block unmuted autoplay before the visitor interacts.
            // Controls remain available, and selecting the slide retries playback.
          });
        }
      } catch (error) {
        // Browser autoplay restrictions can prevent playback.
      }
    });
  }

  function initGallery(gallery) {
    if (!gallery || gallery.__hpnListicleOfferGalleryBound) return;

    const slides = Array.from(gallery.querySelectorAll(GALLERY_SLIDE_SELECTOR));
    const thumbs = Array.from(gallery.querySelectorAll(GALLERY_THUMB_SELECTOR));
    const thumbViewport = gallery.querySelector(GALLERY_THUMB_VIEWPORT_SELECTOR);
    const prevButton = gallery.querySelector(
      `${GALLERY_NAV_SELECTOR}[data-hpn-listicle-offer-gallery-nav="prev"]`
    );
    const nextButton = gallery.querySelector(
      `${GALLERY_NAV_SELECTOR}[data-hpn-listicle-offer-gallery-nav="next"]`
    );

    if (!slides.length) return;

    gallery.__hpnListicleOfferGalleryBound = true;

    let index = slides.findIndex(
      (slide) => slide.getAttribute('data-hpn-listicle-offer-gallery-active') === '1'
    );

    if (index < 0) index = 0;

    function syncNavigation() {
      const maxIndex = slides.length - 1;

      if (prevButton) prevButton.disabled = index <= 0;
      if (nextButton) nextButton.disabled = index >= maxIndex;
    }

    function centerActiveThumb(activeThumb) {
      if (!thumbViewport || !activeThumb) return;

      const viewportRect = thumbViewport.getBoundingClientRect();
      const thumbRect = activeThumb.getBoundingClientRect();
      const targetLeft =
        thumbViewport.scrollLeft +
        (thumbRect.left - viewportRect.left) -
        viewportRect.width / 2 +
        thumbRect.width / 2;

      thumbViewport.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: 'smooth',
      });
    }

    function setActive(nextIndex, moveFocus = false) {
      const maxIndex = slides.length - 1;
      index = Math.min(Math.max(nextIndex, 0), maxIndex);

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;

        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          slide.setAttribute('data-hpn-listicle-offer-gallery-active', '1');
        } else {
          slide.removeAttribute('data-hpn-listicle-offer-gallery-active');
          pauseSlideVideos(slide);
        }
      });

      thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === index;

        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
        thumb.setAttribute('tabindex', isActive ? '0' : '-1');

        if (isActive) {
          thumb.setAttribute('data-hpn-listicle-offer-gallery-active', '1');
        } else {
          thumb.removeAttribute('data-hpn-listicle-offer-gallery-active');
        }
      });

      const activeThumb = thumbs[index];
      centerActiveThumb(activeThumb);

      if (moveFocus && activeThumb) {
        activeThumb.focus({ preventScroll: true });
      }

      playSlideVideos(slides[index]);

      syncNavigation();
    }

    function handleGalleryClick(event) {
      const thumb = event.target.closest(GALLERY_THUMB_SELECTOR);

      if (thumb && gallery.contains(thumb)) {
        const nextIndex = Number.parseInt(
          thumb.getAttribute('data-gallery-index') || '0',
          10
        );

        setActive(Number.isFinite(nextIndex) ? nextIndex : 0);
        return;
      }

      const navButton = event.target.closest(GALLERY_NAV_SELECTOR);
      if (!navButton || !gallery.contains(navButton)) return;

      event.preventDefault();

      const direction = navButton.getAttribute('data-hpn-listicle-offer-gallery-nav');
      setActive(direction === 'prev' ? index - 1 : index + 1);
    }

    function handleGalleryKeydown(event) {
      if (slides.length < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setActive(index - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setActive(index + 1, true);
      } else if (event.key === 'Home') {
        event.preventDefault();
        setActive(0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActive(slides.length - 1, true);
      }
    }

    gallery.__hpnListicleOfferGalleryClickHandler = handleGalleryClick;
    gallery.__hpnListicleOfferGalleryKeydownHandler = handleGalleryKeydown;
    gallery.__hpnListicleOfferGallerySetActive = setActive;

    gallery.addEventListener('click', handleGalleryClick);
    gallery.addEventListener('keydown', handleGalleryKeydown);

    preloadGalleryMedia(slides).then(() => {
      gallery.setAttribute('data-hpn-listicle-offer-gallery-media-ready', '1');
    });

    setActive(index);
  }

  function destroyGallery(gallery) {
    if (!gallery) return;

    if (gallery.__hpnListicleOfferGalleryClickHandler) {
      gallery.removeEventListener(
        'click',
        gallery.__hpnListicleOfferGalleryClickHandler
      );
    }

    if (gallery.__hpnListicleOfferGalleryKeydownHandler) {
      gallery.removeEventListener(
        'keydown',
        gallery.__hpnListicleOfferGalleryKeydownHandler
      );
    }

    gallery.querySelectorAll(GALLERY_SLIDE_SELECTOR).forEach(pauseSlideVideos);

    gallery.__hpnListicleOfferGalleryClickHandler = null;
    gallery.__hpnListicleOfferGalleryKeydownHandler = null;
    gallery.__hpnListicleOfferGallerySetActive = null;
    gallery.__hpnListicleOfferGalleryBound = false;
  }

  function destroySection(section) {
    if (!section) return;

    section.querySelectorAll(TIMER_SELECTOR).forEach((timer) => {
      if (timer.__hpnListicleOfferTimerId) {
        window.clearTimeout(timer.__hpnListicleOfferTimerId);
      }

      timer.__hpnListicleOfferTimerBound = false;
    });

    section.querySelectorAll(GALLERY_SELECTOR).forEach(destroyGallery);

    if (section.__hpnListicleOfferClickHandler) {
      section.removeEventListener(
        'click',
        section.__hpnListicleOfferClickHandler
      );

      section.__hpnListicleOfferClickHandler = null;
    }

    section.__hpnListicleOfferBound = false;
  }

  function initSection(section) {
    if (!section || section.__hpnListicleOfferBound) return;
    section.__hpnListicleOfferBound = true;

    section.querySelectorAll(TIMER_SELECTOR).forEach(initTimer);
    section.querySelectorAll(GALLERY_SELECTOR).forEach(initGallery);

    const clickHandler = (event) => {
      const button = event.target.closest(CTA_SELECTOR);
      if (!button || !section.contains(button)) return;

      event.preventDefault();
      handleCtaClick(button);
    };

    section.__hpnListicleOfferClickHandler = clickHandler;
    section.addEventListener('click', clickHandler);
  }

  function init(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initSection(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SECTION_SELECTOR)
      ? event.target
      : event.target.querySelector?.(SECTION_SELECTOR);

    destroySection(section);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.(GALLERY_SLIDE_SELECTOR);
    const gallery = slide?.closest?.(GALLERY_SELECTOR);

    if (
      !slide ||
      !gallery ||
      typeof gallery.__hpnListicleOfferGallerySetActive !== 'function'
    ) {
      return;
    }

    const selectedIndex = Number.parseInt(
      slide.getAttribute('data-gallery-index') || '0',
      10
    );

    gallery.__hpnListicleOfferGallerySetActive(
      Number.isFinite(selectedIndex) ? selectedIndex : 0
    );
  });

  if (document.readyState !== 'loading') {
    init();
  }
})();
